import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '../../lib/api';
import { Card } from '../../components/ui';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorMessage from '../../components/ErrorMessage';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface OverallAnalytics {
  totalAttendees: number;
  totalCheckIns: number;
  totalSessions: number;
  averageSessionAttendance: number;
  popularSessions: {
    sessionId: string;
    sessionTitle: string;
    checkInCount: number;
  }[];
  checkInsByHour: { hour: number; count: number }[];
  peakArrivalPeriod: number | null;
  roomUtilization: {
    roomId: string;
    roomName: string;
    building: string;
    capacity: number;
    utilizationPercent: number;
  }[];
}

export default function AnalyticsDashboard() {
  const { data: analytics, isLoading, error, refetch } = useQuery<OverallAnalytics>({
    queryKey: ['analytics'],
    queryFn: () => fetchApi<OverallAnalytics>('/admin/analytics'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" message="Loading analytics..." />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load analytics'}
        onRetry={() => refetch()}
      />
    );
  }

  const formatHour = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}${period}`;
  };

  // Calculate total registered attendees for check-in rate
  const totalRegisteredAttendees = analytics?.totalAttendees || 0;
  const checkInRate = totalRegisteredAttendees > 0 
    ? ((analytics?.totalCheckIns || 0) / totalRegisteredAttendees) * 100 
    : 0;

  // Prepare data for charts
  const popularSessionsData = analytics?.popularSessions?.slice(0, 10).map((session) => ({
    name: session.sessionTitle.length > 30 
      ? session.sessionTitle.substring(0, 30) + '...' 
      : session.sessionTitle,
    fullName: session.sessionTitle,
    checkIns: session.checkInCount,
  })) || [];

  const checkInsByHourData = analytics?.checkInsByHour?.map((item) => ({
    hour: formatHour(item.hour),
    checkIns: item.count,
  })) || [];

  const roomUtilizationData = analytics?.roomUtilization?.map((room) => ({
    name: `${room.roomName} (${room.building})`,
    utilization: Math.round(room.utilizationPercent),
    capacity: room.capacity,
  })) || [];

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-usc-garnet">Analytics Dashboard</h2>
        <p className="text-usc-gray-600 mt-2">
          Comprehensive conference statistics and insights
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-usc-gray-600">Total Attendance</p>
              <p className="text-3xl font-bold text-usc-garnet mt-2">
                {analytics?.totalAttendees || 0}
              </p>
              <p className="text-xs text-usc-gray-500 mt-1">Unique attendees</p>
            </div>
            <div className="text-4xl">👥</div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-usc-gray-600">Total Check-ins</p>
              <p className="text-3xl font-bold text-usc-garnet mt-2">
                {analytics?.totalCheckIns || 0}
              </p>
              <p className="text-xs text-usc-gray-500 mt-1">Across all sessions</p>
            </div>
            <div className="text-4xl">✓</div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-usc-gray-600">Check-in Rate</p>
              <p className="text-3xl font-bold text-usc-garnet mt-2">
                {checkInRate.toFixed(1)}%
              </p>
              <p className="text-xs text-usc-gray-500 mt-1">Of registered attendees</p>
            </div>
            <div className="text-4xl">📊</div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-usc-gray-600">Avg Attendance</p>
              <p className="text-3xl font-bold text-usc-garnet mt-2">
                {analytics?.averageSessionAttendance?.toFixed(1) || '0.0'}
              </p>
              <p className="text-xs text-usc-gray-500 mt-1">Per session</p>
            </div>
            <div className="text-4xl">📈</div>
          </div>
        </Card>
      </div>

      {/* Charts Row 1: Popular Sessions and Check-in Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Most Popular Sessions */}
        <Card className="p-6">
          <h3 className="text-xl font-bold text-usc-garnet mb-4">
            Most Popular Sessions
          </h3>
          {popularSessionsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={popularSessionsData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={150}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-3 border border-usc-gray-200 rounded shadow-lg">
                          <p className="font-medium text-usc-gray-900 mb-1">
                            {payload[0].payload.fullName}
                          </p>
                          <p className="text-sm text-usc-garnet">
                            Check-ins: {payload[0].value}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar dataKey="checkIns" fill="#73000a" name="Check-ins" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-usc-gray-600 text-center py-8">No session data available</p>
          )}
        </Card>

        {/* Check-in Trends by Hour */}
        <Card className="p-6">
          <h3 className="text-xl font-bold text-usc-garnet mb-4">
            Check-in Trends by Hour
          </h3>
          {checkInsByHourData.length > 0 ? (
            <>
              {analytics?.peakArrivalPeriod !== null && analytics?.peakArrivalPeriod !== undefined && (
                <div className="mb-4 p-3 bg-usc-gray-50 rounded-lg">
                  <p className="text-sm text-usc-gray-600">
                    Peak Arrival Period: <span className="font-bold text-usc-garnet">
                      {formatHour(analytics.peakArrivalPeriod)}
                    </span>
                  </p>
                </div>
              )}
              <ResponsiveContainer width="100%" height={320}>
                <LineChart
                  data={checkInsByHourData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="checkIns" 
                    stroke="#73000a" 
                    strokeWidth={2}
                    name="Check-ins"
                    dot={{ fill: '#73000a', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : (
            <p className="text-usc-gray-600 text-center py-8">No check-in data available</p>
          )}
        </Card>
      </div>

      {/* Charts Row 2: Room Utilization */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        <Card className="p-6">
          <h3 className="text-xl font-bold text-usc-garnet mb-4">
            Capacity Utilization by Room
          </h3>
          {roomUtilizationData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={roomUtilizationData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  label={{ value: 'Utilization %', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-3 border border-usc-gray-200 rounded shadow-lg">
                          <p className="font-medium text-usc-gray-900 mb-1">
                            {payload[0].payload.name}
                          </p>
                          <p className="text-sm text-usc-garnet">
                            Utilization: {payload[0].value}%
                          </p>
                          <p className="text-sm text-usc-gray-600">
                            Capacity: {payload[0].payload.capacity}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="utilization" 
                  fill="#73000a" 
                  name="Utilization %"
                >
                  {roomUtilizationData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={
                        entry.utilization >= 90 
                          ? '#dc2626' 
                          : entry.utilization >= 70 
                          ? '#f59e0b' 
                          : '#10b981'
                      } 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-usc-gray-600 text-center py-8">No room data available</p>
          )}
        </Card>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-bold text-usc-garnet mb-3">Session Overview</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-usc-gray-600">Total Sessions:</span>
              <span className="font-medium text-usc-gray-900">
                {analytics?.totalSessions || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-usc-gray-600">Avg Attendance:</span>
              <span className="font-medium text-usc-gray-900">
                {analytics?.averageSessionAttendance?.toFixed(1) || '0.0'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-usc-gray-600">Total Check-ins:</span>
              <span className="font-medium text-usc-gray-900">
                {analytics?.totalCheckIns || 0}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-bold text-usc-garnet mb-3">Attendance Metrics</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-usc-gray-600">Unique Attendees:</span>
              <span className="font-medium text-usc-gray-900">
                {analytics?.totalAttendees || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-usc-gray-600">Check-in Rate:</span>
              <span className="font-medium text-usc-gray-900">
                {checkInRate.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-usc-gray-600">Peak Hour:</span>
              <span className="font-medium text-usc-gray-900">
                {analytics?.peakArrivalPeriod !== null && analytics?.peakArrivalPeriod !== undefined
                  ? formatHour(analytics.peakArrivalPeriod)
                  : 'N/A'}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-bold text-usc-garnet mb-3">Room Statistics</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-usc-gray-600">Total Rooms:</span>
              <span className="font-medium text-usc-gray-900">
                {analytics?.roomUtilization?.length || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-usc-gray-600">Avg Utilization:</span>
              <span className="font-medium text-usc-gray-900">
                {analytics?.roomUtilization && analytics.roomUtilization.length > 0
                  ? (
                      analytics.roomUtilization.reduce(
                        (sum, room) => sum + room.utilizationPercent,
                        0
                      ) / analytics.roomUtilization.length
                    ).toFixed(1)
                  : '0.0'}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-usc-gray-600">High Utilization:</span>
              <span className="font-medium text-usc-gray-900">
                {analytics?.roomUtilization?.filter((r) => r.utilizationPercent >= 70).length || 0}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
